// The CEISA edit documents of one Doc Pabean batch, each with its file and an upload button (revamp PR 7a). Same
// requests (the batch's document list on open, a multipart POST per file, a refetch after), file checks and download
// fallback as the Mantine modal it replaces; the dialog and table are Lumen, the copy goes through t(), toasts sonner.
import { File, FileSpreadsheet, FileText, Loader2, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { DataTableMono } from '@/components/data-table';
import { Badge } from '@/components/lumen/badge';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/lumen/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/lumen/tooltip';
import { formatTemplate } from '@/lib/format-template';
import { useLanguage } from '@contexts/LanguageContext';
import axiosInstance from '@libs/axios';

// Backend contract: GET /api/v1/ceisa/edit/document/by-batch-uuid/:batchUUID
// returns []CeisaEditDocumentDto. Documented fields are id +
// documentTypeCode + documentNo + fileUrl + fileName + urlDokumen, but the
// payload may carry extras — keep the type permissive.
type CeisaEditDocument = {
  id: number;
  documentTypeCode?: string;
  documentNo?: string;
  fileUrl?: string;
  fileName?: string;
  urlDokumen?: string | null;
};

type UnggahBerkasModalProps = {
  opened: boolean;
  onClose: () => void;
  batchUUID: string;
};

const fileExt = (url: string, name: string): string => {
  const path = (() => {
    try {
      return new URL(url).pathname;
    } catch {
      return url.split('?')[0];
    }
  })();
  const source = name || path;
  return (source.split('.').pop() || '').toLowerCase();
};

const FileIcon = ({ url, name }: { url: string; name: string }) => {
  const ext = fileExt(url, name);
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv')
    return <FileSpreadsheet aria-hidden className="size-4 shrink-0 text-ok" />;
  if (ext === 'pdf') return <FileText aria-hidden className="size-4 shrink-0 text-err" />;
  return <File aria-hidden className="size-4 shrink-0 text-fg-muted" />;
};

export default function UnggahBerkasModal({ opened, onClose, batchUUID }: UnggahBerkasModalProps) {
  const { t } = useLanguage();
  const [docs, setDocs] = useState<CeisaEditDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  // One shared hidden input — `pendingDocIdRef` stashes which row clicked,
  // so the change handler can route the file to the correct edit-doc.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingDocIdRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!batchUUID) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/v1/ceisa/edit/document/by-batch-uuid/${encodeURIComponent(batchUUID)}`);
      const payload = res.data?.data ?? res.data;
      setDocs(Array.isArray(payload) ? payload : []);
    } catch (caught) {
      const err = caught as { response?: { data?: { error?: { message?: string }; message?: string } } };
      const msg =
        err?.response?.data?.error?.message || err?.response?.data?.message || t('lumen.docPabean.uploadListFailed');
      toast.error(msg);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [batchUUID]);

  useEffect(() => {
    if (opened) refresh();
  }, [opened, refresh]);

  const triggerUpload = (docId: number) => {
    pendingDocIdRef.current = docId;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    const docId = pendingDocIdRef.current;
    pendingDocIdRef.current = null;
    if (!picked || docId == null) return;

    const lower = picked.name.toLowerCase();
    const allowed =
      lower.endsWith('.pdf') || lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv');
    if (!allowed) {
      toast.error(t('lumen.docPabean.uploadBadFormatTitle'), { description: t('lumen.docPabean.uploadBadFormat') });
      return;
    }

    try {
      setUploadingId(docId);
      const fd = new FormData();
      fd.append('file', picked);
      await axiosInstance.post(`/api/v1/ceisa/edit/document/${docId}/file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(formatTemplate(t('lumen.docPabean.uploadDone'), { name: picked.name }));
      // Re-fetch so fileUrl/fileName reflect the freshly uploaded file.
      await refresh();
    } catch (caught) {
      const err = caught as { response?: { data?: { error?: { message?: string }; message?: string } } };
      const msg =
        err?.response?.data?.error?.message || err?.response?.data?.message || t('lumen.docPabean.uploadFailed');
      toast.error(msg);
    } finally {
      setUploadingId(null);
    }
  };

  const handleDownload = async (url: string, name: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = name || `document.${fileExt(url, name) || 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog open={opened} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('lumen.docPabean.uploadTitle')}</DialogTitle>
        </DialogHeader>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.xlsx,.xls,.csv,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={handleFileChange}
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-fg-muted text-xs">
            Batch UUID: <DataTableMono>{batchUUID || '—'}</DataTableMono>
          </span>
          <Button size="xs" variant="secondary" onClick={refresh} disabled={loading}>
            {loading && <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />}
            {t('lumen.docPabean.uploadRefresh')}
          </Button>
        </div>

        <div className="max-h-[420px] overflow-auto rounded-card border border-line">
          <Table>
            <TableHeader className="sticky top-0 z-[1] bg-surface-2">
              <TableRow>
                <TableHead className="w-20 text-center">{t('lumen.docPabean.uploadColSeri')}</TableHead>
                <TableHead>{t('lumen.docPabean.uploadColJenis')}</TableHead>
                <TableHead>{t('lumen.docPabean.uploadColNo')}</TableHead>
                <TableHead>{t('lumen.docPabean.uploadColFile')}</TableHead>
                <TableHead className="w-28 text-center">{t('lumen.docPabean.uploadColAksi')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && docs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <p className="flex items-center justify-center gap-2 py-6 text-fg-muted text-xs">
                      <Loader2 aria-hidden className="size-4 animate-spin motion-reduce:animate-none" />
                      {t('lumen.docPabean.uploadLoading')}
                    </p>
                  </TableCell>
                </TableRow>
              ) : docs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <p className="py-6 text-center text-fg-muted text-xs">{t('lumen.docPabean.uploadEmpty')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                docs.map((d, idx) => {
                  const url = (d.fileUrl || '').trim();
                  const name = (d.fileName || '').trim();
                  const action = url ? t('lumen.docPabean.uploadReplace') : t('lumen.docPabean.uploadUpload');
                  return (
                    <TableRow key={d.id ?? idx}>
                      <TableCell className="text-center text-xs tabular-nums">{idx + 1}</TableCell>
                      <TableCell>
                        <DataTableMono className="font-medium text-xs">{d.documentTypeCode || '-'}</DataTableMono>
                      </TableCell>
                      <TableCell className="whitespace-normal break-words">
                        <DataTableMono className="text-xs">{d.documentNo || '-'}</DataTableMono>
                      </TableCell>
                      <TableCell>
                        {url ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="flex max-w-[220px] items-center gap-1.5 rounded-control text-fg text-xs outline-none focus-visible:ring-[3px] focus-visible:ring-label/40"
                                onClick={() => handleDownload(url, name)}
                              >
                                <FileIcon url={url} name={name} />
                                <span className="truncate underline">{name || 'Download'}</span>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>{name || 'Download'}</TooltipContent>
                          </Tooltip>
                        ) : (
                          <Badge variant="neutral">{t('lumen.docPabean.uploadNoFile')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={action}
                              data-action="upload-file"
                              className="text-label hover:text-label"
                              disabled={uploadingId === d.id}
                              onClick={() => triggerUpload(d.id)}
                            >
                              {uploadingId === d.id ? (
                                <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />
                              ) : (
                                <Upload aria-hidden />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{action}</TooltipContent>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('lumen.docPabean.uploadClose')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
